import { gql } from '@apollo/client';
import type { FC } from 'react';

import { useMatch } from 'react-router';
import { CurrentPressroomContextProvider } from '../../../../contexts/CurrentPressroomContext';
import { EnvironmentVariablesContextProvider } from '../../../../contexts/EnvironmentVariablesContext';
import { HiddenMenuContextProvider } from '../../../../contexts/HiddenMenuContext';
import { LoadedFontsContextProvider } from '../../../../contexts/LoadedFontsContext';
import { NoticesContextProvider } from '../../../../contexts/NoticesContext';
import { OutdatedClientContextProvider } from '../../../../contexts/OutdatedClientContext';
import { SystemDialogContextProvider } from '../../../../contexts/SystemDialogContext';
import { UnscheduleRelatedMaterialContextProvider } from '../../../../contexts/UnscheduleRelatedMaterialContext';
import { UseQueryStateProvider } from '../../../../contexts/UseQueryStateContext';
import { UserLocaleAndTimezoneContextProvider } from '../../../../contexts/UserLocaleAndTimezoneContext';
import { useContextContainerQuery } from '../../../../generated/hooks';

/**
 * Data included in this query is data that should, under normal circumstances,
 * never be updated and is intended to prefill the data needed for the first render.
 */
gql`
  query ContextContainer($pressroomId: ID!, $skipPressroom: Boolean!) {
    me {
      id
      user {
        id
        locale2
        timeZone2
      }
    }
    pressroom(id: $pressroomId) @skip(if: $skipPressroom) {
      id
      source {
        id
        trial
      }
    }
    environmentVariables {
      mndAssets {
        apiKey
        cdnDistribution
        postUrl
        preset
      }
    }
  }
`;

const ContextContainer: FC<{ children: React.ReactNode }> = ({ children }) => {
  const match = useMatch<'pressroomId', string>('/:pressroomId/*');
  const pressroomId = Number.isNaN(Number(match?.params.pressroomId))
    ? undefined
    : match?.params.pressroomId;

  const { data } = useContextContainerQuery({
    variables: {
      pressroomId: pressroomId ?? '',
      skipPressroom: !pressroomId,
    },
  });

  return (
    <EnvironmentVariablesContextProvider mndAssets={data?.environmentVariables.mndAssets}>
      <UserLocaleAndTimezoneContextProvider
        locale={data?.me?.user.locale2 ?? undefined}
        timezone={data?.me?.user.timeZone2 ?? undefined}
      >
        <CurrentPressroomContextProvider
          pressroomId={pressroomId}
          sourceId={data?.pressroom?.source?.id}
          trial={data?.pressroom?.source?.trial}
        >
          <UseQueryStateProvider>
            <NoticesContextProvider>
              <SystemDialogContextProvider>
                <UnscheduleRelatedMaterialContextProvider>
                  <LoadedFontsContextProvider>
                    <OutdatedClientContextProvider>
                      <HiddenMenuContextProvider>{children}</HiddenMenuContextProvider>
                    </OutdatedClientContextProvider>
                  </LoadedFontsContextProvider>
                </UnscheduleRelatedMaterialContextProvider>
              </SystemDialogContextProvider>
            </NoticesContextProvider>
          </UseQueryStateProvider>
        </CurrentPressroomContextProvider>
      </UserLocaleAndTimezoneContextProvider>
    </EnvironmentVariablesContextProvider>
  );
};

export default ContextContainer;
